import React, { useState, useEffect, useRef } from "react";
import { Users, ClipboardList, User, TrendingUp, Bell, FileText } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  Input,
  Button,
} from "reactstrap";

import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import DashboardService from "../Dashboard/dashboard.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  AreaChart,
  Area,
} from "recharts";
//fine

const DASHBOARD_TILE_ROUTES = {
  "Total Users": "/patients-list",
  Appointments: "#appointments",
  "Total Service Providers": "/doctors-list",
  Revenue: "/sara-ladger-list",
  "Approval Requests": "/doctors-list",
};

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dateDropdownRef = useRef(null);
  const appointmentsSectionRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  // Card style constants
  const activeBgColor = "#A4F4E9"; // #AAFFF4 or similar aqua from screenshot
  const activeIconColor = "#00BFA6";

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleDateFilterApply = () => {
    fetchDashboardSummary();
    if (dateDropdownRef.current) {
      dateDropdownRef.current.toggle();
    }
  };

  const handleTileClick = (title) => {
    const route = DASHBOARD_TILE_ROUTES[title];
    if (!route) return;

    if (route === "#appointments") {
      appointmentsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    navigate(route);
  };

  const [cardData, setCardData] = useState([]);
  const [appointmentsTrendData, setAppointmentsTrendData] = useState([]);
  const [userDistributionData, setUserDistributionData] = useState([]);
  const [appointmentsTableData, setAppointmentsTableData] = useState([]);
  const [topServicesData, setTopServicesData] = useState([]);
  const [topServicesDates, setTopServicesDates] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [revenueTotals, setRevenueTotals] = useState({});
  const [activePatients, setActivePatients] = useState(0);
  const [jobsCompleted, setJobsCompleted] = useState(0);
  const [avgRating, setAvgRating] = useState("0");
  const [prescriptionsData, setPrescriptionsData] = useState({});
  const [totalPatients, setTotalPatients] = useState(0);
  const [newPatients, setNewPatients] = useState(0);

  useEffect(() => {
    fetchDashboardSummary();
  }, []);

  function fetchDashboardSummary(overrideDateRange = undefined) {
    setLoading(true);

     const payload = {
      functionName: "DashboardSummary",
      postData: {
        date_range:
          overrideDateRange !== undefined
            ? overrideDateRange
            : startDate && endDate
            ? `${startDate.replace(/-/g, "/")} - ${endDate.replace(/-/g, "/")}`
            : null,
      },
    };

    DashboardService.DashboardSummary(payload).then(
      (response) => {
        setLoading(false);
        console.log("Get dashboard summary response:-", response);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          const data = response.data.orderData;

          setCardData([
            {
              title: "Total Users",
              value: data.total_users.toString(),
              subValue: `+${data.this_month_new_users} this month`,
              icon: <Users size={24} className="text-teal-600" />,
              bgColor: "bg-teal-100",
              highlight: true,
            },
            {
              title: "Appointments",
              value: data.total_appointments.toString(),
              subValue: `${data.percentage_completed_appointments}% completed`,
              icon: <ClipboardList size={24} className="text-gray-600" />,
              bgColor: "bg-gray-100",
            },
            {
              title: "Total Service Providers",
              value: data.total_service_providers.toString(),
              icon: <User size={24} className="text-gray-600" />,
              bgColor: "bg-gray-100",
            },
            {
              title: "Revenue",
              value: `RM ${data.this_month_revenue}`,
              icon: <TrendingUp size={24} className="text-gray-600" />,
              bgColor: "bg-gray-100",
            },
            {
              title: "Approval Requests",
              value: data.alerts.toString(),
              subValue: "Pending Service Provider Requests",
              icon: <FileText size={24} className="text-yellow-500" />,
              bgColor: "bg-gray-100",
            },
          ]);

          setAppointmentsTrendData(
            data.appointments_trends.map((item) => ({
              month: item.month,
              value: item.count,
            }))
          );

          setUserDistributionData(
            data.user_distribution.map((item, idx) => {
              const colors = ["#10B981", "#1E3A8A"];
              return { ...item, color: colors[idx % colors.length] };
            })
          );

          setAppointmentsTableData(
            data.appointments.map((a) => ({
              patient: a.patient_name,
              hospital: a.clinic_name,
              doctor: a.doctor_name,
              datetime: new Date(a.datetime_added).toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              }),
              status: a.status,
              statusColor: "#14B8A6",
            }))
          );

          setTopServicesData(
            data.top_services_providers.data.map((item, idx) => {
              const colors = ["#0D9AF4", "#10B981", "#0F172A"];
              return { ...item, color: colors[idx % colors.length] };
            })
          );

          setTopServicesDates(
            getDateRangeArray(
              data.top_services_providers.range.start_date,
              data.top_services_providers.range.end_date
            )
          );

          setRevenueData(data.revenue.data);
          setRevenueTotals(data.revenue.totals);

          setActivePatients(data.active_patients);

          setTotalPatients(data.total_users);
          setNewPatients(data.this_month_new_users);

          setJobsCompleted(data.jobs_completed);
          setAvgRating(data.average_rating);
          setPrescriptionsData({
            total: data.total_issued_prescriptions,
            fulfilled: data.total_fulfilled_prescriptions,
            pending: data.total_pending_prescriptions,
          });
        }
      },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        if (errContent) {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert("Server Error, Please try again later.");
        }
      }
    );
  }

  function getDateRangeArray(start, end) {
    const result = [];
    const [sd, sm, sy] = start.split("/").map(Number);
    const [ed, em, ey] = end.split("/").map(Number);

    let currentDate = new Date(2000 + ey, em - 1, ed);
    const finalDate = new Date(2000 + sy, sm - 1, sd);

    // Fix if start date > end date
    if (currentDate < finalDate) {
      [currentDate, finalDate] = [finalDate, currentDate];
    }

    while (currentDate >= finalDate) {
      const d = currentDate.getDate().toString().padStart(2, "0");
      const m = (currentDate.getMonth() + 1).toString().padStart(2, "0");
      const y = currentDate.getFullYear().toString().slice(-2);
      result.unshift(`${d}/${m}/${y}`);
      currentDate.setDate(currentDate.getDate() - 1);
    }

    return result;
  }

  return (
    <div
      style={{
        marginLeft: "80px",
        width: "calc(100% - 80px)",
        minHeight: "100vh",
        backgroundColor: "#f8f9fa",
        padding: "24px",
        boxSizing: "border-box",
      }}
    >
      <LoadingOverlay
        tag="div"
        active={loading}
        styles={{
          overlay: (base) => ({
            ...base,
            position: "fixed", // This is key
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999, // Ensure it's above everything
          }),
        }}
        spinner={<DNALoader />}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#F9FAFB",
            borderRadius: "20px",
            padding: "20px 32px",
            marginBottom: "32px",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
          }}
        >
          {/* Left: Dashboard Title */}
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1F2937",
              margin: 0,
            }}
          >
            SARA Analytical Dashboard
          </h2>

          {/* Right: Notification + Date Filter */}
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>

            {/* <UncontrolledDropdown>
              <DropdownToggle
                color="link"
                style={{
                  border: "none",
                  background: "#E5E7EB",
                  borderRadius: "16px",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <FontAwesomeIcon icon={faBell} className="text-gray-600" />
                <span
                  style={{
                    position: "absolute",
                    top: "-3px",
                    right: "2px",
                    backgroundColor: "#EF4444",
                    color: "#fff",
                    borderRadius: "50%",
                    fontSize: "10px",
                    width: "16px",
                    height: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                  }}
                >
                  4
                </span>
              </DropdownToggle>

              <DropdownMenu
                end
                style={{
                  padding: "0",
                  width: "320px",
                  borderRadius: "16px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                }}
              >
                {[
                  {
                    title: "Doctor Registrations Pending",
                    description: "5 doctor registrations pending approval.",
                    count: 5,
                  },
                  {
                    title: "Pharmacy Orders Pending",
                    description: "8 pharmacy orders pending delivery",
                    count: 8,
                  },
                  {
                    title: "Payout Pending",
                    description: "3 payout withdrawals pending",
                    count: 3,
                  },
                  {
                    title: "IoT Device Order",
                    description: "Oximeter Perchase Order",
                    count: 2,
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      gap: "12px",
                      alignItems: "flex-start",
                      padding: "16px 20px",
                      borderBottom: index !== 3 ? "1px solid #E5E7EB" : "none",
                      cursor: "pointer",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faBell}
                      className="text-gray-500 mt-1"
                    />
                    <div>
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#1F2937",
                        }}
                      >
                        {item.title}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#6B7280",
                          marginTop: "2px",
                        }}
                      >
                        {item.description}
                      </div>
                    </div>
                  </div>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown> */}

            {/* Date Filter */}
            <UncontrolledDropdown ref={dateDropdownRef}>
              <DropdownToggle
                color="link"
                style={{
                  border: "none",
                  background: "#E5E7EB",
                  borderRadius: "16px",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="text-gray-600"
                />
                <span style={{ fontSize: "14px", color: "#4B5563" }}>
                  {startDate && endDate
                    ? `${startDate} - ${endDate}`
                    : "Filter by date"}
                </span>
              </DropdownToggle>
              <DropdownMenu end className="p-3" style={{ minWidth: "300px" }}>
                <div className="mb-2">
                  <label className="form-label small fw-bold">Start Date</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-control-sm"
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label small fw-bold">End Date</label>
                  <Input
                    type="date"
                    value={endDate}
                    min={startDate} // <-- prevents selecting earlier dates
                    onChange={(e) => setEndDate(e.target.value)}
                    className="form-control-sm"
                  />
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  
                <Button
                  size="sm"
                  color="secondary"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);

                    // Instead of relying on async state update, explicitly send null
                    fetchDashboardSummary(null);

                    if (dateDropdownRef.current) {
                      dateDropdownRef.current.toggle();
                    }
                  }}
                >
                  Reset
                </Button>

                  <Button
                    size="sm"
                    color="success"
                    onClick={handleDateFilterApply}
                    disabled={!startDate || !endDate} // optional: disable if dates not selected
                  >
                    Apply Filter
                  </Button>
                </div>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </div>

        {/* Main Analytics Cards */}

        <div style={{ display: "flex", gap: "24px", marginBottom: "32px" }}>
          {cardData.map(({ title, value, subValue, icon }, idx) => {
            const isActive = idx === activeIndex;
            return (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                onClick={() => handleTileClick(title)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleTileClick(title);
                  }
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                style={{
                  cursor: "pointer",
                  backgroundColor: isActive ? activeBgColor : "#F3F4F6",
                  borderRadius: "16px",
                  padding: "24px",
                  minHeight: "140px",
                  display: "flex",
                  flexDirection: "column",
                  flex: "1",
                  boxShadow: isActive
                    ? "0 4px 12px rgba(0, 191, 166, 0.3)"
                    : "0 2px 4px rgba(0,0,0,0.1)",
                  border: isActive ? "1px solid #00BFA6" : "none",
                  transition: "all 0.3s ease",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: isActive ? "#00BFA6" : "#D1D5DB",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px",
                    color: isActive ? "#fff" : "#6B7280",
                    transition: "all 0.3s ease",
                    fontSize: "28px",
                  }}
                >
                  {React.cloneElement(icon, {
                    size: 32,
                    color: isActive ? "#FFFFFF" : "#6B7280",
                  })}
                </div>

                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4B5563",
                    marginBottom: "4px",
                  }}
                >
                  {title}
                </div>

                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "700",
                    color: "#111827",
                    lineHeight: 1.1,
                  }}
                >
                  {value}
                </div>

                {subValue && (
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#6B7280",
                      marginTop: "auto",
                    }}
                  >
                    {subValue}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Divider line */}
        <div
          style={{ borderTop: "2px solid #E5E7EB", paddingTop: "24px" }}
        ></div>

        {/* Trends Section */}
        <div>
          <h3
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: "#1F2937",
              marginBottom: "32px",
            }}
          >
            Trends
          </h3>

          <div style={{ display: "flex", gap: "32px", height: "400px" }}>
            {/* Appointments Trends Chart */}
            <div
              style={{
                flex: 2,
                backgroundColor: "#FFFFFF",
                borderRadius: "16px",
                padding: "32px",
                boxShadow: "0 6px 12px rgba(0,0,0,0.08)",
                border: "none",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <h4
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#111827",
                  marginBottom: "28px",
                }}
              >
                Appointments Trends
              </h4>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={appointmentsTrendData}
                  margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
                >
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 14, fill: "#6B7280", fontWeight: 600 }}
                    padding={{ left: 10, right: 10 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 14, fill: "#6B7280", fontWeight: 600 }}
                    width={60}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2DD36F"
                    strokeWidth={4}
                    dot={{ fill: "#2DD36F", strokeWidth: 0, r: 6 }}
                    activeDot={{ r: 8, fill: "#2DD36F" }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      borderRadius: 8,
                      borderColor: "#2DD36F",
                      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                      padding: "8px 12px",
                    }}
                    labelFormatter={(label) => `Month: ${label}`}
                    formatter={(value) => [`${value}`, "Appointment"]}
                  />
                  <CartesianGrid horizontal={false} vertical={false} />
                  <ReferenceLine
                    x={new Date().toLocaleString("default", { month: "short" })}
                    stroke="#2DD36F"
                    strokeDasharray="3 3"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* User Distribution Pie Chart */}
            <div
              style={{
                flex: 1,
                backgroundColor: "#FFFFFF",
                borderRadius: "16px",
                padding: "32px",
                boxShadow: "0 6px 12px rgba(0,0,0,0.08)",
                border: "none",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <h4
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#111827",
                  marginBottom: "28px",
                }}
              >
                User Distribution
              </h4>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={userDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={75} // bigger inner radius for donut hole
                    outerRadius={110}
                    dataKey="value"
                    startAngle={90}
                    endAngle={450}
                    paddingAngle={6} // creates gaps between slices
                    // Enable tooltip on hover for slices
                  >
                    {userDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name, props) => {
                      const totalUsers = userDistributionData.reduce(
                        (acc, item) => acc + item.value,
                        0
                      );
                      const percent = ((value / totalUsers) * 100).toFixed(0);
                      return [`${percent}%`, props.payload.name];
                    }}
                    contentStyle={{
                      backgroundColor: "#fff",
                      borderRadius: 8,
                      borderColor: "#111827",
                      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                      padding: "8px 12px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Legend */}
              <div
                style={{
                  marginTop: "24px",
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-around",
                  flexWrap: "nowrap", // prevent wrapping
                  gap: "12px", // space between items
                }}
              >
                {userDistributionData.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap", // prevent text wrap in legend items
                    }}
                  >
                    <div
                      style={{
                        width: "14px", // slightly smaller square
                        height: "14px",
                        backgroundColor: item.color,
                        borderRadius: "4px",
                        marginRight: "6px", // reduced margin
                      }}
                    ></div>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#111827",
                        fontWeight: 600,
                      }}
                    >
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Appointments Table Section */}
        <div
          ref={appointmentsSectionRef}
          id="dashboard-appointments"
          style={{
            marginTop: "40px",
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 6px 12px rgba(0,0,0,0.08)",
            border: "none",
            maxWidth: "100%",
            scrollMarginTop: "24px",
          }}
        >
          <h4
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
              marginBottom: "24px",
            }}
          >
            Appointments
          </h4>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
              color: "#374151",
            }}
          >
            <thead
              style={{
                backgroundColor: "#F3F4F6",
                textAlign: "left",
                borderBottom: "2px solid #E5E7EB",
              }}
            >
              <tr>
                {[
                  "Patient Name",
                  "Hospital Name",
                  "Doctor Name",
                  "Date & Time",
                  "Status",
                ].map((header) => (
                  <th
                    key={header}
                    style={{
                      padding: "12px 16px",
                      fontWeight: "600",
                      color: "#6B7280",
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {appointmentsTableData.map((row, index) => (
                <tr
                  key={index}
                  style={{
                    borderBottom: "1px solid #E5E7EB",
                    cursor: "default",
                    transition: "background-color 0.3s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#F9FAFB")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <td style={{ padding: "12px 16px" }}>{row.patient}</td>
                  <td style={{ padding: "12px 16px" }}>{row.hospital}</td>
                  <td style={{ padding: "12px 16px" }}>{row.doctor}</td>
                  <td style={{ padding: "12px 16px" }}>{row.datetime}</td>
                  <td
                    style={{
                      padding: "12px 16px",
                      color: row.statusColor,
                      fontWeight: "600",
                    }}
                  >
                    {row.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* KPIs Section */}
        <div
          style={{
            marginTop: "40px",
            display: "flex",
            gap: "24px",
            justifyContent: "space-between",
            maxWidth: "100%",
          }}
        >
          {/* Patients Card */}
          <div
            style={{
              flex: 1,
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 6px 12px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <h5
              style={{
                fontWeight: "600",
                color: "#1E293B",
                marginBottom: "12px",
              }}
            >
              Patients
            </h5>

            {[
              { label: "Total Patients", value: totalPatients, dark: true },
              { label: "New Patients", value: newPatients, dark: true },
              { label: "Active Patients", value: activePatients, dark: true },
            ].map(({ label, value, dark }) => (
              <div
                key={label}
                style={{
                  backgroundColor: dark ? "#0F172A" : "#F1F5F9",
                  color: dark ? "#FFFFFF" : "#1E293B",
                  borderRadius: "12px",
                  padding: "12px 24px",
                  fontWeight: "700",
                  fontSize: "18px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: dark ? "0 1px 4px rgba(0,0,0,0.3)" : "none",
                }}
              >
                <span>{label}</span>
                <span>{value}</span>
              </div>
            ))}
          </div>

          {/* Doctors/Nurses Card */}
          <div
            style={{
              flex: 1,
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 6px 12px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <h5
              style={{
                fontWeight: "600",
                color: "#1E293B",
                marginBottom: "12px",
              }}
            >
              Doctors/Service Providers
            </h5>

            {[
              {
                label: "Total Providers",
                value: parseInt(cardData[2]?.value || 0),
              },
              { label: "Jobs Completed", value: jobsCompleted },
            ].map(({ label, value }) => (
              <div
                key={label}
                style={{
                  backgroundColor: "#F1F5F9",
                  color: "#1E293B",
                  borderRadius: "12px",
                  padding: "12px 24px",
                  fontWeight: "700",
                  fontSize: "18px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{label}</span>
                <span>{value}</span>
              </div>
            ))}

            <div
              style={{
                backgroundColor: "#F1F5F9",
                color: "#1E293B",
                borderRadius: "12px",
                padding: "12px 24px",
                fontWeight: "700",
                fontSize: "18px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Average Rating</span>
              <span
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <svg
                  width="20"
                  height="20"
                  fill="#14B8A6"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
                {avgRating}
              </span>
            </div>
          </div>

          {/* Prescriptions Card */}
          <div
            style={{
              flex: 1,
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 6px 12px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <h5
              style={{
                fontWeight: "600",
                color: "#1E293B",
                marginBottom: "12px",
              }}
            >
              Appointment
            </h5>

            {[
              { label: "Total Issued", value: prescriptionsData.total },
              { label: "Fulfilled", value: prescriptionsData.fulfilled },
              { label: "Pending", value: prescriptionsData.pending },
            ].map(({ label, value }) => (
              <div
                key={label}
                style={{
                  backgroundColor: "#F1F5F9",
                  color: "#1E293B",
                  borderRadius: "12px",
                  padding: "12px 24px",
                  fontWeight: "700",
                  fontSize: "18px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{label}</span>
                <span>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Services Providers Section */}
        <div
          style={{
            marginTop: "40px",
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 6px 12px rgba(0,0,0,0.08)",
            border: "none",
            maxWidth: "100%",
          }}
        >
          <h4
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
              marginBottom: "24px",
            }}
          >
            Top Services Providers
          </h4>

          {/* Legend */}
          <div
            style={{
              display: "flex",
              gap: "24px",
              marginBottom: "24px",
              justifyContent: "flex-end",
              alignItems: "center",
              fontSize: "14px",
              color: "#111827",
              fontWeight: 600,
            }}
          >
            {topServicesData.map(({ name, color }, idx) => (
              <div
                key={idx}
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <div
                  style={{
                    width: "18px",
                    height: "18px",
                    backgroundColor: color,
                    borderRadius: "6px",
                  }}
                />
                <span>{name}</span>
                {idx < topServicesData.length - 1 && (
                  <div
                    style={{
                      width: "1px",
                      height: "18px",
                      backgroundColor: "#D1D5DB",
                      marginLeft: "24px",
                    }}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Bar chart */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "18px" }}
          >
            {topServicesData.map(({ name, value, color }) => (
              <div
                key={name}
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <div
                  style={{
                    minWidth: "150px",
                    color: "#9CA3AF",
                    fontSize: "14px",
                  }}
                >
                  {name}
                </div>
                <div
                  style={{
                    height: "30px",
                    borderRadius: "12px",
                    width: `${value}%`,
                    maxWidth: "70%",
                    backgroundColor: color,
                  }}
                />
              </div>
            ))}
          </div>

          {/* All Date Labels */}
          <div
            style={{
              marginTop: "32px",
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
              justifyContent: "flex-start", // tightly packed to the left
              color: "#6B7280",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {topServicesDates.map((date, index) => (
              <span key={index}>{date}</span>
            ))}
          </div>
        </div>

        {/* Revenue Area Chart Section */}
        <div
          style={{
            marginTop: "40px",
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 6px 12px rgba(0,0,0,0.08)",
            border: "none",
            maxWidth: "100%",
          }}
        >
          <h4
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
              marginBottom: "24px",
            }}
          >
            Revenue
          </h4>

          {/* Legend */}
          <div
            style={{
              display: "flex",
              gap: "24px",
              marginBottom: "24px",
              justifyContent: "flex-end",
              alignItems: "center",
              fontSize: "14px",
              color: "#111827",
              fontWeight: 600,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <svg width="16" height="16" style={{ fill: "#0D9AF4" }}>
                <circle cx="8" cy="8" r="8" />
              </svg>
              <span>Total Revenue</span>
              <span style={{ marginLeft: "6px", fontWeight: "700" }}>
                RM {revenueTotals.totalRevenue || "0.00"}
              </span>
            </div>
            <div
              style={{
                width: "1px",
                height: "20px",
                backgroundColor: "#D1D5DB",
                marginLeft: "24px",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <svg width="16" height="16" style={{ fill: "#10B981" }}>
                <circle cx="8" cy="8" r="8" />
              </svg>
              <span>IoT Device Sales</span>
              <span style={{ marginLeft: "6px", fontWeight: "700" }}>
                RM {revenueTotals.totalIot || "0.00"}
              </span>
            </div>
            <div
              style={{
                width: "1px",
                height: "20px",
                backgroundColor: "#D1D5DB",
                marginLeft: "24px",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <svg width="16" height="16" style={{ fill: "#0F172A" }}>
                <circle cx="8" cy="8" r="8" />
              </svg>
              <span>Pharmacy Orders</span>
              <span style={{ marginLeft: "6px", fontWeight: "700" }}>
                RM {revenueTotals.totalPharmacy || "0.00"}
              </span>
            </div>
          </div>

          {/* Area Chart */}
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart
              data={revenueData}
              margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
            >
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#6B7280", fontWeight: 600 }}
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#6B7280", fontWeight: 600 }}
                domain={[0, 13000]}
                ticks={[0, 2500, 5000, 7500, 10000, 12500]}
                width={60}
              />
              <CartesianGrid horizontal={false} vertical={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  borderRadius: 8,
                  borderColor: "#0D9AF4",
                  boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                  padding: "8px 12px",
                }}
                labelFormatter={(label) => `Date: ${label}`}
              />
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0D9AF4" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0D9AF4" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorIOT" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorPharmacy" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0F172A" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0F172A" stopOpacity={0} />
                </linearGradient>
              </defs>

              <Area
                type="monotone"
                dataKey="total"
                stroke="#0D9AF4"
                strokeWidth={3}
                fill="url(#colorTotal)"
                dot={{ r: 5 }}
                activeDot={{ r: 6 }}
              />
              <Area
                type="monotone"
                dataKey="iot"
                stroke="#10B981"
                strokeWidth={3}
                fill="url(#colorIOT)"
                dot={{ r: 5 }}
                activeDot={{ r: 6 }}
              />
              <Area
                type="monotone"
                dataKey="pharmacy"
                stroke="#0F172A"
                strokeWidth={3}
                fill="url(#colorPharmacy)"
                dot={{ r: 5 }}
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </LoadingOverlay>
    </div>
  );
};

export default Dashboard;
